import React, { useState } from "react";

import {
	Badge,
	ConfirmModal,
	Select,
	Table,
	TTableColumn,
} from "@amphore-dev/amphore-lib";
import { format } from "date-fns";
import i18n, { t } from "i18next";
import { useNavigate } from "react-router-dom";

import {
	useInfiniteQuery,
	useMutation,
	useQueryClient,
} from "@tanstack/react-query";

import InvoicesService from "@/services/InvoicesService";

import { usePlanGate } from "@/hooks/usePlanGate";

import { ClientSelect } from "@/components/molecules";
import { InvoiceStatusBadge } from "@/components/molecules/InvoiceStatusBadge/InvoiceStatusBadge";
import { PlanFeatureBanner } from "@/components/organisms/PlanGate/PlanFeatureBanner";
import { PageLayout } from "@/components/templates";

import { INVOICE_LIST_PAGE_SIZE } from "@/constants/CInvoices";
import { invoiceKeys } from "@/constants/CQueryKeys";

import { TCompany, TInvoice, TInvoiceStatusFilter } from "@/types";

import { promiseToast } from "@/modules/index";
import { downloadBlob, formatMoney } from "@/utils";

import { InvoiceSummaryTiles } from "./components/InvoiceSummaryTiles";

const STATUS_FILTERS: (TInvoiceStatusFilter | "all")[] = [
	"all",
	"draft",
	"finalized",
	"unpaid",
	"paid",
];

const dateCell = (value: string | null) =>
	value ? (
		<span className="text-sm text-neutral-600">
			{format(new Date(value), "dd/MM/yyyy")}
		</span>
	) : (
		<span className="text-sm text-neutral-400">—</span>
	);

export const InvoicesView: React.FC = () => {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const [deleteTarget, setDeleteTarget] = useState<TInvoice | null>(null);
	const [status, setStatus] = useState<TInvoiceStatusFilter | "all">("all");
	const [client, setClient] = useState<TCompany | null>(null);
	const gate = usePlanGate("invoices");

	const filters = {
		status: status === "all" ? undefined : status,
		clientId: client?.id,
	};

	const { isLoading, data, fetchNextPage, hasNextPage } = useInfiniteQuery({
		queryKey: invoiceKeys.list(filters),
		queryFn: ({ pageParam }) =>
			InvoicesService.list({
				...filters,
				page: pageParam,
				limit: INVOICE_LIST_PAGE_SIZE,
			}),
		getNextPageParam: (lastPage, pages) => {
			const next = pages.length + 1;
			return next <= lastPage.pages ? next : undefined;
		},
		initialPageParam: 1,
	});

	const items = data?.pages.flatMap((p) => p.data) ?? [];

	const invalidate = () =>
		Promise.all([
			queryClient.invalidateQueries({ queryKey: invoiceKeys.all }),
			queryClient.invalidateQueries({ queryKey: invoiceKeys.summary }),
		]);

	const deleteMutation = useMutation({
		mutationFn: (id: number) => InvoicesService.delete(id),
		onSuccess: invalidate,
	});

	const paidMutation = useMutation({
		mutationFn: ({ id, paid }: { id: number; paid: boolean }) =>
			InvoicesService.setPaid(id, paid),
		onSuccess: invalidate,
	});

	const generateMutation = useMutation({
		mutationFn: (id: number) => InvoicesService.generateById(id),
		onSuccess: (response) => {
			const disposition = response.headers["content-disposition"];
			const match = disposition?.match(/filename="(.+)"/);
			downloadBlob(response.data, match?.[1] ?? "invoice.pdf");
		},
	});

	const columns: TTableColumn<TInvoice>[] = [
		{ key: "contextMenu" },
		{
			key: "number",
			label: t("invoices.columns.number"),
			render: (item) => (
				<div className="flex items-center gap-2">
					<span
						className={
							item.number ? "font-medium" : "text-neutral-400"
						}
					>
						{item.number ?? t("invoices.draft")}
					</span>
					{item.report && (
						<Badge
							color="neutral"
							size="sm"
							title={item.report.name}
						>
							{t("reports.title")}
						</Badge>
					)}
				</div>
			),
			width: "20%",
		},
		{
			key: "client",
			label: t("invoices.columns.client"),
			render: (item) =>
				item.client ? (
					<div className="flex items-center gap-2">
						{item.client.logo && (
							<img
								src={item.client.logo}
								alt={item.client.name}
								className="h-6 w-6 object-contain"
							/>
						)}
						<span className="text-sm">{item.client.name}</span>
					</div>
				) : (
					<span className="text-sm text-neutral-400">—</span>
				),
		},
		{
			key: "issuedAt",
			label: t("invoices.columns.issuedAt"),
			render: (item) => dateCell(item.issuedAt),
		},
		{
			key: "dueAt",
			label: t("invoices.columns.dueAt"),
			render: (item) => dateCell(item.dueAt),
		},
		{
			key: "totalTtc",
			label: t("invoices.columns.total"),
			render: (item) => (
				<span className="text-sm font-medium tabular-nums">
					{formatMoney(item.totalTtc, item.currency, i18n.language)}
				</span>
			),
		},
		{
			key: "status",
			label: t("invoices.columns.status"),
			render: (item) => <InvoiceStatusBadge invoice={item} />,
		},
		{
			key: "createdAt",
			label: t("invoices.columns.createdAt"),
			render: (item) => dateCell(item.createdAt),
		},
	];

	return (
		<>
			<PageLayout
				title={t("invoices.title")}
				isLoading={isLoading}
				buttons={[
					// Locked off-plan: opens the upgrade modal. The API refuses regardless.
					gate.button({
						label: t("invoices.new"),
						color: "primary",
						onClick: () => navigate("/invoices/new"),
					}),
				]}
			>
				<div className="flex flex-col gap-6">
					<PlanFeatureBanner on="invoices" />
					<InvoiceSummaryTiles />

					<div className="flex flex-wrap items-end gap-3">
						<Select<TInvoiceStatusFilter | "all">
							label={t("invoices.filters.status")}
							options={STATUS_FILTERS.map((value) => ({
								value,
								label: t(`invoices.status.${value}`),
							}))}
							value={status}
							onChange={(value) =>
								setStatus(
									(Array.isArray(value) ? value[0] : value) ??
										"all"
								)
							}
							className="w-48"
						/>
						<ClientSelect
							label={t("invoices.filters.client")}
							value={client}
							onChange={(value) =>
								setClient(
									Array.isArray(value)
										? (value[0] ?? null)
										: value
								)
							}
							className="w-64"
						/>
					</div>

					<Table
						isLoading={isLoading}
						items={items}
						getItemKey={(item) => item.id}
						onRowClick={(item) => navigate(`/invoices/${item.id}`)}
						onLoadMore={fetchNextPage}
						hasMore={hasNextPage}
						columns={columns}
						noDataMessage={t("invoices.noResults")}
						rowActions={[
							{
								label: t("invoices.actions.download"),
								picto: "download",
								onClick: (item: TInvoice) =>
									promiseToast(
										generateMutation.mutateAsync(item.id)
									),
							},
							{
								label: t("invoices.actions.markPaid"),
								picto: "checkCircle",
								onClick: (item: TInvoice) => {
									if (item.status !== "finalized") return;
									promiseToast(
										paidMutation.mutateAsync({
											id: item.id,
											paid: true,
										})
									);
								},
							},
							{
								label: t("invoices.actions.markUnpaid"),
								picto: "refresh",
								onClick: (item: TInvoice) => {
									if (item.status !== "paid") return;
									promiseToast(
										paidMutation.mutateAsync({
											id: item.id,
											paid: false,
										})
									);
								},
							},
							{
								label: t("actions.edit"),
								picto: "pen",
								onClick: (item: TInvoice) =>
									navigate(`/invoices/${item.id}`),
							},
							{
								label: t("actions.delete"),
								picto: "trash",
								onClick: (item: TInvoice) => {
									if (!item.isDraft) return;
									setDeleteTarget(item);
								},
							},
						]}
					/>
				</div>
			</PageLayout>
			<ConfirmModal
				open={!!deleteTarget}
				onClose={() => setDeleteTarget(null)}
				size="sm"
				title={t("invoices.deleteModal.title")}
				onConfirm={() => {
					promiseToast(deleteMutation.mutateAsync(deleteTarget!.id));
					setDeleteTarget(null);
				}}
				confirmText={t("actions.delete")}
				confirmColor="danger"
				cancelText={t("actions.cancel")}
			/>
		</>
	);
};
